import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { CloseIcon } from '../components/Icon';
import Spinner from '../components/Spinner';
import axios from '../lib/axios';

export default class Widget extends Component {
  static propTypes = {
    widgetEndpointUrl: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    initialConfigurations: PropTypes.object.isRequired,
    noDataIconPath: PropTypes.string.isRequired,
    renderMainScreen: PropTypes.func.isRequired,
    renderConfigurationScreen: PropTypes.func,
  };

  static defaultProps = {
    renderConfigurationScreen() {},
  };

  constructor(props) {
    super(props);

    this.state = {
      configs: this.props.initialConfigurations,
      data: [],
      loading: false,
      currentView: 'main',
    };

    this.hasConfigurations = Object.keys(this.state.configs).length > 0;
  }

  // On initialization: fetch data and mark loading: true
  componentDidMount() {
    this.loadInitialData();
  }

  loadInitialData = () => {
    this.setState(
      {
        loading: true,
      },
      this.handleInitialLoad
    );
  };

  // On callback: store data and mark loading: false

  handleInitialLoad = () => {
    axios.get(this.props.widgetEndpointUrl, {}).then(response => {
      this.setState({
        loading: false,
        data: response.data,
      });
    });
  };

  // When configurations are saved:
  // - send data to server and wait for response
  // - change to main screen and load the data again

  // TODO in the future: send only changed properties
  saveConfigurations = e => {
    e.preventDefault();

    this.setState({ loading: true });

    axios
      .put(this.props.widgetEndpointUrl, { configurations: this.state.configs })
      .then(response => {
        this.setState(
          {
            currentView: 'main',
            configs: response.data,
          },
          this.loadInitialData
        );
      });
  };

  // Rendering functions

  renderCurrentView() {
    if (this.state.loading) {
      return this.renderLoading();
    }

    if (this.state.currentView === 'main') {
      return this.renderMainScreen();
    }
    if (this.state.currentView === 'info') {
      return this.renderInfoScreen();
    }
    if (this.state.currentView === 'configuration') {
      return this.renderConfigurationScreen();
    }
  }

  // Render Main screen

  renderMainScreen() {
    if (this.state.data.length === 0) {
      return this.renderNoDataScreen();
    }
    return this.props.renderMainScreen(this.state.data);
  }

  renderNoDataScreen() {
    return (
      <div className="widget-no-data">
        <div>
          <img src={this.props.noDataIconPath} alt="No Data" />
          This widget will be populated with the data from your community.
          <br />
          There&apos;s not enough information to show yet.
        </div>
      </div>
    );
  }

  // Render Configuration screen

  renderConfigurationScreen() {
    return (
      <div className="widget-configs">
        {this.props.renderConfigurationScreen(this.state.configs, this.updateConfigHandler)}
      </div>
    );
  }

  updateConfigHandler = (newConfigs) => {
    this.setState({ configs: Object.assign(this.state.configs, newConfigs) });
  }

  // Render Info screen

  renderInfoScreen() {
    // Render the Main Screen too so Info Screen can be a transparent overlay on
    // top of it
    return (
      <span>
        {this.renderMainScreen()}
        <div className="widget-info">
          <CloseIcon className="widget-info-close-btn" onClick={this.closeInfoScreen} />
          <div>
            <div className="widget-info-title">{this.props.title}</div>
            <div className="widget-info-description">{this.props.description}</div>
          </div>
        </div>
      </span>
    );
  }

  closeInfoScreen = e => {
    e.preventDefault();

    this.setState({ currentView: 'main' });
  };

  // Render Header

  renderHeader() {
    return (
      <div className="widget-header">
        <span className="widget-header-title">{this.props.title}</span>
        <span className="widget-header-actions">
          {this.state.currentView === 'main' && this.renderConfigurationsButton()}
          {this.state.currentView === 'main' && (
            <a onClick={this.showInfoScreen}>
              <i className="Icon Icon-info u-cursor-pointer" />
            </a>
          )}

          {this.state.currentView === 'configuration' && (
            <a className="widget-save-settings-btn" onClick={this.saveConfigurations}>
              Save Settings
            </a>
          )}
        </span>
      </div>
    );
  }

  renderConfigurationsButton() {
    if (this.hasConfigurations) {
      return (
        <a onClick={this.showConfigurationScreen}>
          <i className="Icon Icon-settings u-cursor-pointer" />
        </a>
      );
    }

    return <i className="Icon Icon-settings u-opacity-25" />;
  }

  showInfoScreen = e => {
    e.preventDefault();

    this.setState({ currentView: 'info' });
  };

  showConfigurationScreen = e => {
    e.preventDefault();

    this.setState({ currentView: 'configuration' });
  };

  // Render Loading screen

  renderLoading() {
    return (
      <div className="widget-loading">
        <div>
          <Spinner />
          Loading content
        </div>
      </div>
    );
  }

  // Main rendering function

  render() {
    return (
      <div className={this.props.doubleWidth ? 'widget-double-width' : 'widget'}>
        {this.renderHeader()}
        <div className={`widget-content ${this.state.currentView !== 'info' && 'u-relative'}`}>
          {this.renderCurrentView()}
        </div>
      </div>
    );
  }
}
